"use client";

import { clsx } from "clsx";
import { useTranslations } from "next-intl";
import { Button } from "@ui/components/core/Button";
import { Link } from "@components/core/Link";
import ChevronLeft16 from "@ui/icons/16x16/chevron-left-16.svg";
import { appUrls } from "@/constants";
import { ProgressBar } from "./ProgressIndicator";
import {
  testVariantBackButtonClassname,
  testVariantFooterActionRowClassname,
  testVariantFooterLinkClassname,
  testVariantPrimaryButtonClassname,
} from "./field-variants";
import type { UseContactSalesFormReturn } from "../hooks/useContactSalesForm";
import { captureEvent } from "@/app/_analytics/analytics";
import { EventName } from "@mw/shared/analyticsEventNames";
import type { ContactSalesFormValues } from "../shared/types";

type FormStateType = UseContactSalesFormReturn["form"]["state"];

type ButtonConfig = {
  type: "next" | "back" | "submit";
  label: string;
  variant: "primary" | "secondary" | "outline";
  validateFields?: (keyof ContactSalesFormValues)[];
  onClick?: () => void | Promise<void>;
};

type NavigationConfig = {
  showProgressBar: boolean;
  buttons: ButtonConfig[];
};

type TestVariantFormFooterProps = {
  navigation: NavigationConfig;
  currentStep: number;
  totalSteps: number;
  form: UseContactSalesFormReturn["form"];
};

function TestVariantNextButton({
  buttonConfig,
  form,
  label,
}: {
  buttonConfig: ButtonConfig;
  form: UseContactSalesFormReturn["form"];
  label: string;
}) {
  const { validateFields, onClick } = buttonConfig;

  if (!validateFields) return null;

  return (
    <form.Subscribe
      selector={(state: FormStateType) =>
        validateFields.every(fieldName => state.fieldMeta[fieldName]?.isValid === true)
      }
    >
      {canMoveNext => (
        <Button
          type='button'
          variant='primary'
          size='lg-compact'
          expand
          disabled={!canMoveNext}
          className={testVariantPrimaryButtonClassname}
          onClick={() => onClick && void onClick()}
        >
          {label}
        </Button>
      )}
    </form.Subscribe>
  );
}

function TestVariantSubmitButton({ form, label }: { form: UseContactSalesFormReturn["form"]; label: string }) {
  return (
    <form.Subscribe
      selector={(state: FormStateType) => ({
        canSubmit: state.canSubmit,
        isSubmitting: state.isSubmitting,
      })}
    >
      {({ canSubmit, isSubmitting }) => (
        <Button
          type='submit'
          variant='primary'
          size='lg-compact'
          expand
          disabled={!canSubmit || isSubmitting}
          className={testVariantPrimaryButtonClassname}
        >
          {label}
        </Button>
      )}
    </form.Subscribe>
  );
}

export function TestVariantFormFooter({ navigation, currentStep, totalSteps, form }: TestVariantFormFooterProps) {
  const t = useTranslations("ClientKeys.contact_sales_page.form");

  const nextButton = navigation.buttons.find(button => button.type === "next");
  const backButton = navigation.buttons.find(button => button.type === "back");
  const isStep1 = currentStep === 1;

  return (
    <div className='tw-flex tw-flex-col tw-gap-4 sm:tw-gap-8 tw-w-full tw-shrink-0 tw-pt-6'>
      {isStep1 && nextButton ? (
        <div className={testVariantFooterActionRowClassname}>
          <TestVariantNextButton buttonConfig={nextButton} form={form} label={t("buttons.lets_go")} />
        </div>
      ) : (
        <div className={clsx(testVariantFooterActionRowClassname, "tw-gap-4")}>
          {backButton && (
            <Button
              type='button'
              variant='secondary'
              size='lg-compact'
              className={testVariantBackButtonClassname}
              aria-label={t("buttons.go_back")}
              onClick={backButton.onClick}
            >
              <ChevronLeft16 className='tw-size-4' aria-hidden='true' />
            </Button>
          )}
          <div className='tw-min-w-0 tw-flex-1 tw-h-11'>
            <TestVariantSubmitButton form={form} label={t("buttons.talk_to_our_team")} />
          </div>
        </div>
      )}

      <div className='tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-4 max-lg:tw-px-0 lg:tw-px-3 max-lg:tw-pt-2'>
        <p className='tw-type-2xs md:tw-type-sm tw-text-gray-500 tw-tracking-tight'>
          {t("footer.need_something_else")}{" "}
          <Link
            href={appUrls.supportPage}
            target='_blank'
            rel='noopener noreferrer'
            variant='functional'
            size='sm'
            className={clsx(testVariantFooterLinkClassname, "hover:tw-text-black tw-transition-colors md:tw-text-sm")}
            onClick={() => captureEvent(EventName.mw_contact_sales_v2_help_center_clicked, { cs_v2_step: currentStep })}
          >
            {t("footer.help_center")}
          </Link>
        </p>
        {navigation.showProgressBar && (
          <ProgressBar currentStep={currentStep} totalSteps={totalSteps} variant='testVariant' />
        )}
      </div>
    </div>
  );
}
